/**
 * Acta de Incautación en hoja carta (8.5x11) con el estilo de la casa.
 *
 * La imprimen el registro (ares5010, con el recibo que devuelve el guardado)
 * y la reimpresión del mantenimiento (ares5080, vía /api/mantincauto_recibo).
 * Incluye todos los datos del préstamo, los datos generales del cliente que
 * capturó la solicitud (contacto, estado civil y trabajo, para ubicarlo), el
 * garante (cuando el préstamo tiene fiador) y la foto del cliente
 * (expedienteunico), cuando existe.
 */

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export function imprimirHtml(html) {
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

function htmlActa(d) {
  const compania = d.compania || {};
  const NAVY = "#18586b";
  const foto = (d.foto_b64 || "").trim();
  const fotoHtml = foto
    ? `<img src="data:image/*;base64,${foto}" alt="Foto del cliente" />`
    : '<div class="sinfoto">Sin foto</div>';
  const filaDato = (k, v) =>
    v || v === 0
      ? `<div class="dato"><span>${k}</span><b>${v}</b></div>`
      : "";

  // El garante responde por la deuda: va en el acta cuando la solicitud lo
  // registró. Los préstamos personales y los que se dieron sin fiador no
  // pintan la sección en vez de dejar campos vacíos.
  const dirGarante = [d.fiadordir, d.urbfiad].filter(Boolean).join(", ");
  const hayGarante = Boolean(d.fiador || d.fiadorced || dirGarante || d.fiadortel);
  const garanteHtml = hayGarante
    ? `<div class="seccion">
            <h2>Datos del Garante</h2>
            <div class="grid">
              ${filaDato("Nombre", d.fiador)}
              ${filaDato("Cédula", d.fiadorced)}
              ${filaDato("Dirección", dirGarante)}
              ${filaDato("Teléfono", d.fiadortel)}
            </div>
          </div>`
    : "";
  // Datos generales del cliente (solicitud). Solo se pintan los que existen:
  // un préstamo migrado o sin solicitud no deja huecos con etiquetas vacías.
  const dirCliente = [d.direccion, d.urb].filter(Boolean).join(", ");
  const tiempoTrab =
    d.tiempotrab && String(d.tiempotrab) !== "0"
      ? `${d.tiempotrab} año${String(d.tiempotrab) === "1" ? "" : "s"}`
      : "";
  const edad = d.edad && String(d.edad) !== "0" ? `${d.edad} años` : "";
  const trabajoHtml = [d.empresa, d.dirtrab, d.teltrab, tiempoTrab].some(Boolean)
    ? `<div class="grid" style="margin-top:8px">
              ${filaDato("Lugar de trabajo", d.empresa)}
              ${filaDato("Dirección del trabajo", d.dirtrab)}
              ${filaDato("Teléfono del trabajo", d.teltrab)}
              ${filaDato("Tiempo en el trabajo", tiempoTrab)}
            </div>`
    : "";
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Acta de Incautación</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 180px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .cuerpo { display: flex; gap: 18px; margin-top: 14px; align-items: flex-start; }
      .datos { flex: 1; }
      .fotobox { width: 130px; flex-shrink: 0; text-align: center; }
      .fotobox img { width: 130px; height: 160px; object-fit: cover; border: 1px solid #d7dee5; border-radius: 6px; }
      .fotobox .sinfoto { width: 130px; height: 160px; border: 1px dashed #b9c4cd; border-radius: 6px;
                          display: flex; align-items: center; justify-content: center; color: #8a97a3; font-size: 11px; }
      .fotobox .pie { margin-top: 4px; font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; }
      .seccion { border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px; margin-bottom: 12px; }
      .seccion h2 { margin: 0 0 8px; font-size: 11px; color: ${NAVY}; text-transform: uppercase; letter-spacing: 0.08em; }
      .grid { display: flex; flex-wrap: wrap; gap: 8px 28px; }
      .dato span { display: block; font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; }
      .dato b { font-size: 12px; }
      .nota { margin-top: 10px; font-size: 10px; color: #4a5866; }
      .firmas { display: flex; justify-content: space-between; gap: 20px; margin-top: 70px; }
      .firmas div { flex: 1; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          <h1>${compania.nombre || ""}</h1>
          ${compania.direccion ? `<p>${compania.direccion}</p>` : ""}
          <p>${[compania.rnc ? `RNC: ${compania.rnc}` : "", compania.telefono ? `Tel: ${compania.telefono}` : ""]
            .filter(Boolean)
            .join(" · ")}</p>
        </div>
        <div class="doc">
          <div class="titulo">Acta de Incautación</div>
          <p>Fecha: <b>${d.fecha || ""}</b></p>
          ${d.sucursal ? `<p>Sucursal: <b>${d.sucursal}</b></p>` : ""}
          ${d.id ? `<p>Documento: <b>${d.id}</b></p>` : ""}
        </div>
      </div>
      <div class="cuerpo">
        <div class="datos">
          <div class="seccion">
            <h2>Datos del Cliente</h2>
            <div class="grid">
              ${filaDato("Nombre", d.cliente)}
              ${filaDato("Cédula", d.cedula)}
              ${filaDato("Apodo", d.apodo)}
              ${filaDato("Estado civil", d.ecivil)}
              ${filaDato("Edad", edad)}
              ${filaDato("Dirección", dirCliente)}
              ${filaDato("Teléfono", d.telefono)}
              ${filaDato("Celular", d.celular)}
              ${filaDato("Email", d.email)}
              ${filaDato("Dirección de los padres", d.dirpadres)}
            </div>
            ${trabajoHtml}
          </div>
          ${garanteHtml}
          <div class="seccion">
            <h2>Datos del Préstamo</h2>
            <div class="grid">
              ${filaDato("# Préstamo", d.noprest)}
              ${filaDato("Fecha del préstamo", d.fechaprestamo)}
              ${filaDato("Monto del préstamo", d.cantsol ? "RD$ " + fmtMoney(d.cantsol) : "")}
              ${filaDato("Balance actual", d.balance || d.balance === 0 ? "RD$ " + fmtMoney(d.balance) : "")}
              ${filaDato("Atraso vencido", d.atraso || d.atraso === 0 ? "RD$ " + fmtMoney(d.atraso) : "")}
            </div>
          </div>
          <div class="seccion">
            <h2>Datos del Vehículo</h2>
            <div class="grid">
              ${filaDato("Chasis", d.chasis)}
              ${filaDato("Marca", d.marca)}
              ${filaDato("Modelo", d.modelo)}
              ${filaDato("Color", d.colore)}
            </div>
          </div>
          <div class="seccion">
            <h2>Incautación</h2>
            <div class="grid">
              ${filaDato("Incautador", d.incautador)}
              ${filaDato("Cédula del incautador", d.cedincautador)}
            </div>
          </div>
        </div>
        <div class="fotobox">
          ${fotoHtml}
          <div class="pie">Foto del cliente</div>
        </div>
      </div>
      <p class="nota">El vehículo descrito queda incautado en garantía del préstamo indicado, conforme a las
      condiciones pactadas en el contrato. Este documento fue autorizado por la administración.</p>
      <div class="firmas">
        <div>Incautador</div>
        <div>Cliente</div>
        <div>Autorizado por</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  return html;
}

export function imprimirActaIncauto(d) {
  imprimirHtml(htmlActa(d));
}

/**
 * Varias actas en un solo trabajo de impresión, una por página (detalle de
 * una solicitud de incauto por lote).
 */
export function imprimirActasIncauto(lista) {
  if (!lista || !lista.length) return;
  if (lista.length === 1) {
    imprimirActaIncauto(lista[0]);
    return;
  }
  const docs = lista.map(htmlActa);
  const cabeza = docs[0].split("<body>")[0];
  const paginas = docs
    .map((h, i) => {
      const cuerpo = h.split("<body>")[1].split("<script>")[0];
      const corte = i < docs.length - 1 ? ' style="page-break-after: always"' : "";
      return `<div class="pagina"${corte}>${cuerpo}</div>`;
    })
    .join("");
  imprimirHtml(`${cabeza}<body>${paginas}</body></html>`);
}

export default imprimirActaIncauto;

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Orden de incautación de un lote (solicitudes de incauto): hoja de ruta
 * con todos los préstamos por ejecutar y, después, una ficha por préstamo
 * con lo necesario para ubicar al cliente y el vehículo.
 */
export function imprimirOrdenLote(lote, lineas) {
  const NAVY = "#18586b";
  const hoy = new Date().toLocaleDateString("es-DO");
  const unir = (...xs) => xs.filter((x) => String(x || "").trim()).join(", ");
  const vehiculo = (l) => [l.marca, l.modelo, l.colore].filter(Boolean).join(" ");
  const filas = lineas
    .map(
      (l, i) => `<tr>
        <td>${i + 1}</td><td>${esc(l.noprest)}</td><td>${esc(l.cliente)}</td>
        <td>${esc(unir(l.direccion, l.urb))}</td><td>${esc(unir(l.celular, l.telefono))}</td>
        <td>${esc(vehiculo(l))}<br/><small>${esc(l.chasis)}</small></td>
        <td class="num">${esc(l.dias)}</td><td class="chk"></td></tr>`
    )
    .join("");
  const dato = (k, v) =>
    String(v || "").trim() ? `<div class="dato"><span>${k}</span><b>${esc(v)}</b></div>` : "";
  const fichas = lineas
    .map(
      (l) => `<div class="ficha">
        <h3>Préstamo ${esc(l.noprest)} — ${esc(l.cliente)}</h3>
        <div class="grid">
          ${dato("Cédula", l.cedula)}
          ${dato("Celular", l.celular)}
          ${dato("Teléfono", l.telefono)}
          ${dato("Dirección", unir(l.direccion, l.urb))}
          ${dato("Vehículo", vehiculo(l))}
          ${dato("Chasis", l.chasis)}
          ${dato("Días de atraso", l.dias)}
          ${dato("Monto vencido", fmtMoney(l.vencido))}
          ${dato("Garante", l.fiador)}
          ${dato("Tel. garante", l.fiadortel)}
          ${dato("Dir. garante", unir(l.fiadordir, l.urbfiad))}
        </div>
        <div class="notas">Observaciones: ______________________________________________</div>
      </div>`
    )
    .join("");
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"/>
    <title>Orden de incautación — Lote ${esc(lote.id)}</title>
    <style>
      @page { size: letter; margin: 12mm; }
      body { font-family: Arial, sans-serif; color: #111; font-size: 11px; }
      h1 { color: ${NAVY}; font-size: 18px; margin: 0 0 4px; }
      h3 { color: ${NAVY}; font-size: 13px; margin: 0 0 6px; }
      .cab { display: flex; justify-content: space-between; border-bottom: 2px solid ${NAVY};
             padding-bottom: 6px; margin-bottom: 10px; }
      .cab div { line-height: 1.5; }
      table { width: 100%; border-collapse: collapse; }
      th { background: ${NAVY}; color: #fff; text-align: left; padding: 5px; font-size: 10px; }
      td { border-bottom: 1px solid #ddd; padding: 5px; vertical-align: top; }
      td.num { text-align: right; } td.chk { width: 40px; border: 1px solid #999; }
      .ficha { page-break-inside: avoid; border: 1px solid #ccc; border-radius: 6px;
               padding: 10px; margin-top: 10px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; }
      .dato span { color: #666; display: block; font-size: 9px; text-transform: uppercase; }
      .notas { margin-top: 8px; color: #555; }
      .fichas { page-break-before: always; }
      .firma { margin-top: 40px; display: flex; gap: 60px; }
      .firma div { border-top: 1px solid #333; padding-top: 4px; width: 220px; text-align: center; }
    </style></head><body>
    <div class="cab">
      <div><h1>Orden de incautación</h1>
        <b>Lote #${esc(lote.id)}</b> · ${esc(lote.motivo)}<br/>
        Incautador: <b>${esc(lote.incautador)}</b> (${esc(lote.cedincaut)})</div>
      <div style="text-align:right">Fecha de solicitud: ${esc(lote.fecha)}<br/>
        ${lote.fechalimite ? `Fecha límite: <b>${esc(lote.fechalimite)}</b><br/>` : ""}
        Aprobado por: ${esc(lote.aprobadopor || "—")}<br/>Impreso: ${hoy}</div>
    </div>
    ${lote.nota ? `<p>${esc(lote.nota)}</p>` : ""}
    <table><thead><tr><th>#</th><th>Préstamo</th><th>Cliente</th><th>Dirección</th>
      <th>Teléfonos</th><th>Vehículo</th><th>Días</th><th>Hecho</th></tr></thead>
      <tbody>${filas}</tbody></table>
    <div class="firma"><div>Entregado por</div><div>Recibido por (incautador)</div></div>
    <div class="fichas">${fichas}</div>
  </body></html>`;
  imprimirHtml(html);
}
